import { useState, useEffect } from "react";
import { useAuth, useDateFilter, API } from "../App";
import Layout from "../components/Layout";
import DateRangePicker from "../components/DateRangePicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Package, TrendingUp, DollarSign, ChevronDown, ChevronRight, Globe, Building2, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ProductThumbnail } from "../components/ProductThumbnail";
import axios from "axios";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupplierDashboardPage = () => {
  const { token, user } = useAuth();
  const { startDate, endDate } = useDateFilter();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhysical, setExpandedPhysical] = useState({});
  const [expandedOnline, setExpandedOnline] = useState({});
  const [inventory, setInventory] = useState(null);
  
  // Admin can select user type and specific user
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState('proveedor'); // 'manager' or 'proveedor'
  const isAdmin = user?.role === 'admin';
  const isActualSupplier = user?.role === 'proveedor';

  const togglePhysicalExpand = (productId) => {
    setExpandedPhysical(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleOnlineExpand = (productId) => {
    setExpandedOnline(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  // Fetch users list for admin
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  // Reset selected user when user type changes
  useEffect(() => {
    setSelectedUserId(null);
  }, [selectedUserType]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => u.is_active));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Get filtered users by type
  const filteredUsers = users.filter(u => u.role === selectedUserType);

  useEffect(() => {
    // For proveedor role viewing their own data
    if (isActualSupplier) {
      fetchSummary();
      return;
    }
    // For admin: only fetch if a user is selected and type is proveedor
    if (isAdmin && selectedUserType === 'proveedor' && !selectedUserId) return;
    // For manager type, we show the main dashboard (redirect or show message)
    if (isAdmin && selectedUserType === 'manager') return;
    fetchSummary();
  }, [startDate, endDate, selectedUserId, selectedUserType]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      let url = `${API_URL}/api/supplier/dashboard?start_date=${startStr}&end_date=${endStr}`;
      // If admin viewing a provider, add provider_id param
      if (isAdmin && selectedUserId) {
        url += `&provider_id=${selectedUserId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
      // Fetch inventory restricted to is_proveedor products
      try {
        let invUrl = `${API_URL}/api/proveedor/inventory`;
        if (isAdmin && selectedUserId) invUrl += `?provider_id=${selectedUserId}`;
        const inv = await axios.get(invUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInventory(inv.data);
      } catch (_) {
        setInventory(null);
      }
    } catch (error) {
      console.error("Error fetching supplier summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  // Detect if every product is on fixed_per_piece scheme.
  // Combines physical_sales + online_sales products_summary.
  const allProducts = [
    ...(summary?.physical_sales?.products_summary || []),
    ...(summary?.online_sales?.products_summary || []),
  ];
  const isAllFixed = allProducts.length > 0 && allProducts.every(p => p.payout_type === "fixed_per_piece");

  // Prepare pie chart data for distribution
  const distributionData = [
    { name: "Costo a Recuperar", value: summary?.total_cost_recovery || 0, color: "#8B5CF6" },
    { name: "Tu Margen", value: summary?.total_supplier_margin || 0, color: "#10B981" }
  ].filter(d => d.value > 0);

  // Expandable table component with correct columns
  const SalesTable = ({ title, icon: Icon, iconColor, sales, expanded, toggleExpand, emptyMessage, isOnline }) => {
    // Detect if every product within THIS table is on fixed_per_piece scheme.
    const tableAllFixed = sales?.products_summary?.length > 0 &&
      sales.products_summary.every(p => p.payout_type === "fixed_per_piece");
    return (
    <Card className="bg-white border-[#FECDD3] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-[#4A4A4A] text-lg flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          {title}
          {sales?.total_units_sold > 0 && (
            <span className="text-sm font-normal text-[#6B7280] ml-2">
              ({sales.total_units_sold} piezas)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sales?.products_summary?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#FECDD3]">
                  <th className="text-left py-3 px-2 text-sm font-medium text-[#6B7280]">Producto / Variante</th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-[#6B7280]">Piezas</th>
                  {!tableAllFixed && (
                    <th className="text-right py-3 px-2 text-sm font-medium text-[#6B7280]">Monto Total</th>
                  )}
                  {!tableAllFixed && (
                    <th className="text-right py-3 px-2 text-sm font-medium text-[#6B7280]">
                      Utilidad Darkar Co.
                      <span className="block text-xs font-normal">
                        {isOnline ? "(Merch)" : "(Heladería)"}
                      </span>
                    </th>
                  )}
                  <th className="text-right py-3 px-2 text-sm font-medium text-[#6B7280]">
                    Utilidad Proveedor
                    <span className="block text-xs font-normal">
                      {isOnline ? "(50%)" : "(Merch 50%)"}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sales.products_summary.map((product) => (
                  <>
                    <tr 
                      key={product.product_id} 
                      className={`border-b border-[#FECDD3]/50 cursor-pointer transition-colors ${
                        expanded[product.product_id] ? 'bg-[#FEE2E2]/50' : 'hover:bg-[#FEE2E2]/30'
                      }`}
                      onClick={() => toggleExpand(product.product_id)}
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {product.variants && product.variants.length > 0 ? (
                            expanded[product.product_id] ? (
                              <ChevronDown className="w-4 h-4 text-[#F43F5E]" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                            )
                          ) : (
                            <span className="w-4" />
                          )}
                          <ProductThumbnail image_url={product.image_url} name={product.product_name} size="sm" />
                          <span className="font-medium text-[#4A4A4A]">{product.product_name}</span>
                          {product.variants && product.variants.length > 0 && (
                            <span className="text-xs text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded">
                              {product.variants.length} variantes
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-[#8B5CF6] font-medium">
                        {product.units_sold.toLocaleString()}
                      </td>
                      {!tableAllFixed && (
                        <td className="py-3 px-2 text-right font-mono text-[#4A4A4A]">
                          {product.payout_type === "fixed_per_piece" ? (
                            <span className="text-[#6B7280] text-xs">
                              {product.units_sold} × {formatCurrency(product.payout_value)}
                            </span>
                          ) : (
                            formatCurrency(product.gross_total)
                          )}
                        </td>
                      )}
                      {!tableAllFixed && (
                        <td className="py-3 px-2 text-right font-mono text-[#F59E0B]">
                          {product.payout_type === "fixed_per_piece" ? (
                            <span className="text-[#6B7280]">—</span>
                          ) : (
                            formatCurrency(product.darkar_margin)
                          )}
                        </td>
                      )}
                      <td className="py-3 px-2 text-right font-mono text-[#10B981]">
                        {formatCurrency(product.supplier_margin)}
                      </td>
                    </tr>
                    
                    {expanded[product.product_id] && product.variants?.map((variant, idx) => (
                      <tr 
                        key={`${product.product_id}-variant-${idx}`}
                        className="bg-[#FFFAFA] border-b border-[#FECDD3]/30"
                      >
                        <td className="py-2 px-2 pl-10">
                          <span className="text-sm text-[#6B7280] flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#8B5CF6]"></span>
                            {variant.variant_name}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-sm text-[#8B5CF6]">
                          {variant.units_sold.toLocaleString()}
                        </td>
                        {!tableAllFixed && (
                          <td className="py-2 px-2 text-right font-mono text-sm text-[#6B7280]">
                            {product.payout_type === "fixed_per_piece" ? (
                              <span className="text-xs">
                                {variant.units_sold} × {formatCurrency(product.payout_value)}
                              </span>
                            ) : (
                              formatCurrency(variant.gross_total)
                            )}
                          </td>
                        )}
                        {!tableAllFixed && (
                          <td className="py-2 px-2 text-right font-mono text-sm text-[#F59E0B]/80">
                            {product.payout_type === "fixed_per_piece" ? (
                              <span className="text-[#9CA3AF]">—</span>
                            ) : (
                              formatCurrency(variant.darkar_margin)
                            )}
                          </td>
                        )}
                        <td className="py-2 px-2 text-right font-mono text-sm text-[#10B981]/80">
                          {formatCurrency(variant.supplier_margin)}
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#F9FAFB] font-medium">
                  <td className="py-3 px-2 text-[#4A4A4A]">Total</td>
                  <td className="py-3 px-2 text-right font-mono text-[#8B5CF6]">
                    {sales.total_units_sold.toLocaleString()}
                  </td>
                  {!tableAllFixed && (
                    <td className="py-3 px-2 text-right font-mono text-[#4A4A4A]">
                      {formatCurrency(sales.total_gross)}
                    </td>
                  )}
                  {!tableAllFixed && (
                    <td className="py-3 px-2 text-right font-mono text-[#F59E0B]">
                      {formatCurrency(sales.total_darkar_margin)}
                    </td>
                  )}
                  <td className="py-3 px-2 text-right font-mono text-[#10B981]">
                    {formatCurrency(sales.total_supplier_margin)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-[#6B7280]">
            {emptyMessage}
          </div>
        )}
      </CardContent>
    </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A]">
              {isAdmin ? "Vista Usuario" : "Dashboard de Proveedor"}
            </h1>
            <p className="text-sm text-[#6B7280]">
              {isAdmin 
                ? selectedUserType === 'manager' 
                  ? "Los Gerentes Heladería ven el mismo dashboard principal"
                  : `Viendo como: ${selectedUser?.name || 'Selecciona un proveedor'}`
                : `Bienvenido, ${user?.name}`
              }
            </p>
            
            {/* Admin: User Type and User Selector */}
            {isAdmin && (
              <div className="mt-3 flex flex-wrap gap-3">
                {/* User Type Selector */}
                <Select value={selectedUserType} onValueChange={setSelectedUserType}>
                  <SelectTrigger className="w-48 bg-white border-[#FECDD3]" data-testid="user-type-select">
                    <SelectValue placeholder="Tipo de usuario" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#FECDD3]">
                    <SelectItem value="manager" className="hover:bg-[#FEE2E2]">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#3B82F6]" />
                        <span>Gerente Heladería</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="proveedor" className="hover:bg-[#FEE2E2]">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#10B981]" />
                        <span>Proveedor</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* User Selector (only for proveedor type) */}
                {selectedUserType === 'proveedor' && (
                  <Select value={selectedUserId || ""} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="w-64 bg-white border-[#FECDD3]" data-testid="provider-select">
                      <Users className="w-4 h-4 mr-2 text-[#6B7280]" />
                      <SelectValue placeholder="Seleccionar proveedor" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#FECDD3]">
                      {filteredUsers.map(p => (
                        <SelectItem key={p.id} value={p.id} className="hover:bg-[#FEE2E2]">
                          <div className="flex items-center gap-2">
                            <span>{p.name}</span>
                            <span className="text-xs text-[#6B7280]">
                              ({(p.assigned_categories || []).length} productos)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="p-2 text-sm text-[#6B7280]">No hay proveedores configurados</div>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            
            {/* Product tags (only for proveedor view) */}
            {(selectedUserType === 'proveedor' || isActualSupplier) && summary?.assigned_categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-[#6B7280]">Productos asignados:</span>
                {summary?.assigned_categories?.map((cat) => (
                  <span key={cat.id} className="text-xs bg-[#F43F5E]/10 text-[#F43F5E] px-2 py-1 rounded-full">
                    {cat.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <DateRangePicker />
        </div>

        {/* Manager View Message */}
        {isAdmin && selectedUserType === 'manager' ? (
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="p-8 text-center">
              <Building2 className="w-16 h-16 text-[#3B82F6]/30 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-[#4A4A4A] mb-2">Vista Gerente Heladería</h2>
              <p className="text-[#6B7280] mb-4">
                Los Gerentes Heladería tienen acceso al Dashboard principal con las mismas funciones que ves actualmente.
              </p>
              <p className="text-sm text-[#9CA3AF]">
                Pueden ver: Dashboard, Gastos, Ventas y Configuración (sin gestión de usuarios)
              </p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-4 border-[#F43F5E] border-t-transparent rounded-full"></div>
          </div>
        ) : (isAdmin && selectedUserType === 'proveedor' && !selectedUserId) ? (
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 text-[#10B981]/30 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-[#4A4A4A] mb-2">Selecciona un Proveedor</h2>
              <p className="text-[#6B7280]">
                Elige un proveedor del selector de arriba para ver su dashboard.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-[#FECDD3] shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#6B7280]">Piezas Vendidas</p>
                      <p className="text-2xl font-bold text-[#4A4A4A]">{summary?.total_units_sold || 0}</p>
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-[#3B82F6]">Físicas: {summary?.physical_sales?.total_units_sold || 0}</span>
                        <span className="text-[#8B5CF6]">Online: {summary?.online_sales?.total_units_sold || 0}</span>
                      </div>
                    </div>
                    <Package className="w-10 h-10 text-[#F43F5E]/20" />
                  </div>
                </CardContent>
              </Card>

              {!isAllFixed && (
                <Card className="bg-white border-[#FECDD3] shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#6B7280]">Venta Bruta Total</p>
                        <p className="text-2xl font-bold text-[#4A4A4A]">{formatCurrency(summary?.total_gross)}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-[#F43F5E]/20" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {!isAllFixed && (
                <Card className="bg-white border-[#FECDD3] shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#6B7280]">Costo a Recuperar</p>
                        <p className="text-2xl font-bold text-[#8B5CF6]">{formatCurrency(summary?.total_cost_recovery)}</p>
                        <div className="flex gap-2 mt-1 text-xs">
                          <span className="text-[#3B82F6]">Físicas: {formatCurrency(summary?.physical_sales?.total_cost)}</span>
                          <span className="text-[#8B5CF6]">Online: {formatCurrency(summary?.online_sales?.total_cost)}</span>
                        </div>
                      </div>
                      <DollarSign className="w-10 h-10 text-[#8B5CF6]/20" />
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-white border-[#FECDD3] shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#6B7280]">Tu Margen Total</p>
                      <p className="text-2xl font-bold text-[#10B981]">{formatCurrency(summary?.total_supplier_margin)}</p>
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-[#3B82F6]">Físicas: {formatCurrency(summary?.physical_sales?.total_supplier_margin)}</span>
                        <span className="text-[#8B5CF6]">Online: {formatCurrency(summary?.online_sales?.total_supplier_margin)}</span>
                      </div>
                    </div>
                    <TrendingUp className="w-10 h-10 text-[#10B981]/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Total to Receive Card */}
            <Card className="bg-gradient-to-r from-[#ECFDF5] to-[#D1FAE5] border-[#10B981]/30 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#059669]">
                      {isAllFixed ? "Total a Recibir" : "Total a Recibir (Costo + Tu Margen)"}
                    </p>
                    <p className="text-3xl font-bold text-[#059669]">
                      {formatCurrency((summary?.total_cost_recovery || 0) + (summary?.total_supplier_margin || 0))}
                    </p>
                  </div>
                  {!isAllFixed && (
                    <div className="text-right text-sm">
                      <p className="text-[#6B7280]">Costo: {formatCurrency(summary?.total_cost_recovery)}</p>
                      <p className="text-[#6B7280]">Tu Margen: {formatCurrency(summary?.total_supplier_margin)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Charts Row - hidden when all products are fixed-cost */}
            {!isAllFixed && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Distribution Pie Chart */}
              <Card className="bg-white border-[#FECDD3] shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#4A4A4A] text-lg">Distribución de Tu Ingreso</CardTitle>
                </CardHeader>
                <CardContent>
                  {distributionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={distributionData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={50}
                          paddingAngle={2}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={true}
                        >
                          {distributionData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-[#6B7280]">
                      No hay datos para mostrar
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Comparison by Channel */}
              <Card className="bg-white border-[#FECDD3] shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#4A4A4A] text-lg">Tu Margen por Canal</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[
                      { 
                        name: 'Tienda Física', 
                        margen: summary?.physical_sales?.total_supplier_margin || 0
                      },
                      { 
                        name: 'Online (Darkar.co)', 
                        margen: summary?.online_sales?.total_supplier_margin || 0
                      }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#FECDD3" />
                      <XAxis dataKey="name" tick={{ fill: '#6B7280', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="margen" fill="#10B981" name="Tu Margen" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Physical Sales Table */}
            <SalesTable 
              title="Ventas en Tienda Física (Darkar Heladería)"
              icon={Building2}
              iconColor="text-[#3B82F6]"
              sales={summary?.physical_sales}
              expanded={expandedPhysical}
              toggleExpand={togglePhysicalExpand}
              emptyMessage="No hay ventas físicas en este período"
              isOnline={false}
            />

            {/* Online Sales Table */}
            <SalesTable 
              title="Ventas Online (Darkar.co)"
              icon={Globe}
              iconColor="text-[#8B5CF6]"
              sales={summary?.online_sales}
              expanded={expandedOnline}
              toggleExpand={toggleOnlineExpand}
              emptyMessage="No hay ventas online en este período"
              isOnline={true}
            />

            {/* Inventory available */}
            {inventory?.items?.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="text-xl font-bold text-[#4A4A4A] font-['Manrope']">Inventario Disponible</h2>
                  {inventory.last_sync && (
                    <span className="text-xs text-[#6B7280]">
                      Última sync: {new Date(inventory.last_sync).toLocaleString('es-MX')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#6B7280] mb-4">Piezas en existencia para los productos asignados a este proveedor, sumando todas las ubicaciones (Odoo + Shopify).</p>
                <div className="space-y-3">
                  {inventory.items.map((it) => (
                    <div key={it.internal_product_id} className="border border-[#E5E7EB] rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <ProductThumbnail image_url={it.image_url} name={it.internal_product_name} size="sm" />
                          <h3 className="font-semibold text-[#4A4A4A]">{it.internal_product_name}</h3>
                        </div>
                        <span className="text-lg font-bold text-[#3B82F6] font-mono">
                          {it.total_available.toFixed(0)} pzs
                        </span>
                      </div>
                      {it.variants?.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {it.variants.map((v) => (
                            <div key={v.sku} className={`p-2 rounded border ${v.available <= 0 ? 'border-[#EF4444]/30 bg-[#FEE2E2]/40' : 'border-[#E5E7EB] bg-[#F9FAFB]'}`}>
                              <p className="text-xs text-[#6B7280] truncate" title={v.label}>{v.label}</p>
                              <p className={`text-base font-bold font-mono ${v.available <= 0 ? 'text-[#EF4444]' : 'text-[#4A4A4A]'}`}>
                                {Number(v.available).toFixed(0)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default SupplierDashboardPage;
